import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { Label, UpsertLabel } from "./types";

const useLabelApi = () => {
  const baseurl = "/labels";

  const api = useApi();

  const searchLabels = useCallback(
    async (query: PaginationQuery, signal?: AbortSignal) => {
      const url = buildUrl(baseurl, query);
      const data = await api.get<Paginated<Label>>({ url, signal });
      return data.items;
    },
    [api]
  );

  const useLabels = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl, query);
      const result = useQuery<Paginated<Label>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<Label>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api]
  );

  const createLabel = useCallback(
    async (l: UpsertLabel) => {
      const data = await api.post<UpsertLabel, string>(baseurl, l);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const updateLabel = useCallback(
    async (l: UpsertLabel, labelID: string) => {
      const data = await api.put<UpsertLabel>(`${baseurl}/${labelID}`, l);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const deleteLabel = useCallback(
    async (labelID: string) => {
      const data = await api.del(`${baseurl}/${labelID}`);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  return {
    searchLabels,
    useLabels,
    createLabel,
    updateLabel,
    deleteLabel,
  };
};

export default useLabelApi;
